import dayjs from 'dayjs';
import { Badge, Box, Checkbox, IconButton, Table, Text } from '@chakra-ui/react';
import { Client } from '@opanuj/shared/types';
import { FiCopy } from 'react-icons/fi';
import { formatPhoneNumber, getClientEmailList, getClientLessonsPoolCount } from './clientTableUtils';
import { MdAccountBox, MdOutlineAccountBox } from 'react-icons/md';
import { memo } from 'react';
import { toaster } from '../../../components/ui/toaster';

export type ClientRowProps = {
	client: Client & { openedReservations?: number };
	selected: boolean;
	previewed: boolean;
	onSelectClient: (clientId: string) => void;
	onToggleSelection: (clientId: string, checked: boolean) => void;
	onOpenDetails: (client: Client) => void;
	openedReservations?: number;
};

const ClientRow = memo((props: ClientRowProps) => {
	const { client, selected, previewed, onSelectClient, onToggleSelection, onOpenDetails, openedReservations = 0 } = props;
	const createdAtTs = dayjs(client.createdAt);
	const emailList = getClientEmailList(client);
	const primaryEmail = emailList[0] || client?.email || 'Brak danych';
	const formattedPhone = formatPhoneNumber(client?.phone);

	const copyToClipboard = async (value: string) => {
		if (!value) return;
		try {
			await navigator.clipboard.writeText(value);
			toaster.create({
				title: 'Skopiowano do schowka',
				description: value,
				type: 'success',
				duration: 2000,
			});
		} catch (error) {
			console.error('Clipboard copy failed', error);
			toaster.create({
				title: 'Nie udało się skopiować',
				type: 'error',
				duration: 2000,
			});
		}
	};

	const handleRowClick = (event: React.MouseEvent<HTMLTableRowElement>) => {
		const target = event.target as HTMLElement;
		if (target.closest('button, input, label, textarea')) {
			return;
		}
		onSelectClient(client.id);
	};
	const hasAccountAccess = Boolean(client?.fullAccount);
	const lessonsCount = client?.products && typeof client.products === 'object' ? Object.keys(client.products).length : 0;
	const lessonsPoolCount = getClientLessonsPoolCount(client);

	return (
		<Table.Row
			key={client.id}
			bg={previewed ? 'cyan.50' : selected ? 'orange.100' : undefined}
			_hover={{ bg: previewed ? 'cyan.100' : selected ? 'orange.200' : 'gray.100' }}
			css={{ '& td': { py: 1.5 } }}
			onClick={handleRowClick}
			onDoubleClick={() => onOpenDetails(client)}
		>
			<Table.Cell py={1.5}>
				<Checkbox.Root
					checked={selected}
					onCheckedChange={(details) => onToggleSelection(client.id, details.checked === true)}
					onClick={(event) => event.stopPropagation()}
				>
					<Checkbox.HiddenInput />
					<Checkbox.Control />
				</Checkbox.Root>
			</Table.Cell>
			<Table.Cell>{createdAtTs.locale('pl').format('DD.MM.YYYY')}</Table.Cell>
			<Table.Cell>
				<Box display="flex" alignItems="center" gap={2}>
					{hasAccountAccess ? <MdAccountBox color="#6b7280" size={18} /> : <MdOutlineAccountBox size={18} color="#6b7280" />}
					<Text fontSize="sm" color='gray.600'>
						{hasAccountAccess ? 'Tak' : 'Nie'}
					</Text>
				</Box>
			</Table.Cell>
			<Table.Cell>
				<Badge colorPalette={openedReservations > 0 ? 'orange' : 'gray'} variant="subtle" borderRadius="full" px={2}>
					{openedReservations}
				</Badge>
			</Table.Cell>
			<Table.Cell>
				<Badge colorPalette={lessonsCount > 0 ? 'green' : 'gray'} variant="subtle" borderRadius="full" px={2}>
					{lessonsCount}
				</Badge>
			</Table.Cell>
			<Table.Cell>
				<Badge colorPalette={lessonsPoolCount > 0 ? 'blue' : 'gray'} variant="subtle" borderRadius="full" px={2}>
					{lessonsPoolCount}
				</Badge>
			</Table.Cell>
			<Table.Cell>
				{client.name}
			</Table.Cell>
			<Table.Cell>
				<Box display="flex" alignItems="center" gap={2} _hover={{ '& button': { opacity: 1 } }}>
					<Text flex={1}>{primaryEmail}
						<IconButton
							aria-label={`Kopiuj email ${primaryEmail}`}
							ml={1}
							size="2xs"
							variant="ghost"
							opacity={0}
							onClick={(event) => {
								event.stopPropagation();
								copyToClipboard(primaryEmail);
							}}
						>
							<FiCopy />
						</IconButton>
					</Text>
				</Box>
			</Table.Cell>
			<Table.Cell>
				<Box display="flex" alignItems="center" gap={2} _hover={{ '& button': { opacity: 1 } }}>
					<Text flex={1}>{formattedPhone}
						<IconButton
							aria-label={`Kopiuj numer telefonu ${formattedPhone}`}
							size="2xs"
							ml={1}
							variant="ghost"
							opacity={0}
							onClick={(event) => {
								event.stopPropagation();
								copyToClipboard(client?.phone ? client.phone.replace(/\D/g, '') : '');
							}}
						>
							<FiCopy />
						</IconButton>
					</Text>
				</Box>
			</Table.Cell>
			<Table.Cell w="100%" minW="100%" />
		</Table.Row>
	);
});

export default ClientRow;
